/* eslint-disable max-lines */
import { z } from "zod";

// Warehouse Summary
export const SqlWarehouseSummarySchema = z.object({
  warehouse_id: z.string(),
  warehouse_name: z.string(),
  workspace_id: z.string(),
  workspace_name: z.string().nullable().optional(),
  warehouse_type: z.string().nullable().optional(),
  warehouse_size: z.string().nullable().optional(),
  min_num_clusters: z.number().nullable().optional(),
  max_num_clusters: z.number().nullable().optional(),
  auto_stop_mins: z.number().nullable().optional(),
  state: z.string().nullable().optional(),
  num_clusters: z.number().nullable().optional(),
  num_active_sessions: z.number().nullable().optional(),
  last_state_change: z.string().nullable().optional(),
  total_dbus: z.number().nullable().optional(),
  estimated_cost: z.number().nullable().optional(),
  tags: z.record(z.string()).nullable().optional(),
  median_queueing_time_ms: z.number().nullable().optional(),
  median_execution_time_ms: z.number().nullable().optional(),
  median_total_time_ms: z.number().nullable().optional(),
  active_seconds: z.number().nullable().optional(),
  idle_seconds: z.number().nullable().optional(),
  query_count: z.number().nullable().optional(),
});
export const SqlWarehousesResponseSchema = z.object({
  warehouses: z.array(SqlWarehouseSummarySchema),
  total_count: z.number(),
  page: z.number(),
  page_size: z.number(),
  total_pages: z.number(),
});
export type SqlWarehouseSummary = z.infer<typeof SqlWarehouseSummarySchema>;
export type SqlWarehousesResponse = z.infer<typeof SqlWarehousesResponseSchema>;
export const SqlWarehouseDetailSchema = z.object({
  warehouse_id: z.string(),
  warehouse_name: z.string(),
  workspace_id: z.string(),
  workspace_name: z.string().nullable().optional(),
  instance_id: z.number().nullable().optional(),
  warehouse_type: z.string().nullable().optional(),
  warehouse_size: z.string().nullable().optional(),
  min_num_clusters: z.number().nullable().optional(),
  max_num_clusters: z.number().nullable().optional(),
  auto_stop_mins: z.number().nullable().optional(),
  state: z.string().nullable().optional(),
  num_clusters: z.number().nullable().optional(),
  num_active_sessions: z.number().nullable().optional(),
  last_state_change: z.string().nullable().optional(),
  creator_name: z.string().nullable().optional(),
  spot_instance_policy: z.string().nullable().optional(),
  enable_serverless_compute: z.boolean().nullable().optional(),
});
export type SqlWarehouseDetail = z.infer<typeof SqlWarehouseDetailSchema>;
export const SqlWarehouseFilterOptionsSchema = z.object({
  warehouse_names: z.array(z.string()).default([]),
  warehouse_types: z.array(z.string()).default([]),
  warehouse_sizes: z.array(z.string()).default([]),
});
export type SqlWarehouseFilterOptions = z.infer<typeof SqlWarehouseFilterOptionsSchema>;
export const WarehouseTypeSchema = z.enum(["serverless", "pro", "classic"]);
export type WarehouseType = z.infer<typeof WarehouseTypeSchema>;
export const SqlWarehouseCostTrendItemSchema = z.object({
  period: z.string(),
  warehouse_type: WarehouseTypeSchema,
  total_dbus: z.number().default(0),
  estimated_cost: z.number().default(0),
});
export const SqlWarehouseCostTrendsResponseSchema = z.object({
  trends: z.array(SqlWarehouseCostTrendItemSchema),
  warehouse_types: z.array(WarehouseTypeSchema),
  granularity: z.string(),
  total_dbus: z.number().default(0),
  total_cost: z.number().default(0),
});
export type SqlWarehouseCostTrendItem = z.infer<typeof SqlWarehouseCostTrendItemSchema>;
export type SqlWarehouseCostTrendsResponse = z.infer<typeof SqlWarehouseCostTrendsResponseSchema>;
export const SqlWarehouseEventSchema = z.object({
  event_time: z.string(),
  event_type: z.string(),
  cluster_count: z.number().nullable().optional(),
});
export const SqlWarehouseEventsResponseSchema = z.object({
  events: z.array(SqlWarehouseEventSchema),
  total_count: z.number().optional(),
});
export type SqlWarehouseEvent = z.infer<typeof SqlWarehouseEventSchema>;
export type SqlWarehouseEventsResponse = z.infer<typeof SqlWarehouseEventsResponseSchema>;
export const SqlWarehouseCostPointSchema = z.object({
  period: z.string(),
  total_dbus: z.number().default(0),
  estimated_cost: z.number().default(0),
});
export const SqlWarehouseCostResponseSchema = z.object({
  warehouse_id: z.string(),
  total_dbus: z.number().default(0),
  estimated_cost: z.number().default(0),
  daily_average_dbus: z.number().default(0),
  daily_average_cost: z.number().default(0),
  timeseries: z.array(SqlWarehouseCostPointSchema).default([]),
});
export type SqlWarehouseCostPoint = z.infer<typeof SqlWarehouseCostPointSchema>;
export type SqlWarehouseCostResponse = z.infer<typeof SqlWarehouseCostResponseSchema>;
export const SqlWarehouseUtilizationPointSchema = z.object({
  period: z.string(),
  utilization: z.number().default(0),
});
export const SqlWarehouseUtilizationEfficiencyResponseSchema = z.object({
  warehouse_id: z.string(),
  trends: z.array(SqlWarehouseUtilizationPointSchema).default([]),
  average_utilization: z.number().default(0),
});
export type SqlWarehouseUtilizationPoint = z.infer<typeof SqlWarehouseUtilizationPointSchema>;
export type SqlWarehouseUtilizationEfficiencyResponse = z.infer<
  typeof SqlWarehouseUtilizationEfficiencyResponseSchema
>;
export const UnifiedQueryRecordSchema = z.object({
  statement_id: z.string(),
  statement_text: z.string().nullable().optional(),
  statement_type: z.string().nullable().optional(),
  execution_status: z.string().nullable().optional(),
  start_time: z.string(),
  end_time: z.string().nullable().optional(),
  total_duration_ms: z.number().nullable().optional(),
  read_bytes: z.number().nullable().optional(),
  spilled_local_bytes: z.number().nullable().optional(),
  workspace_id: z.string().nullable().optional(),
  workspace_name: z.string().nullable().optional(),
  warehouse_id: z.string().nullable().optional(),
  warehouse_name: z.string().nullable().optional(),
  warehouse_size: z.string().nullable().optional(),
  dbus: z.number().nullable().optional(),
  estimated_cost_usd: z.number().nullable().optional(),
  user_name: z.string().nullable().optional(),
  error_message: z.string().nullable().optional(),
  insights: z.array(z.string()).default([]),
});
export const UnifiedQueriesResponseSchema = z.object({
  queries: z.array(UnifiedQueryRecordSchema),
  total_count: z.number(),
  page: z.number().default(1),
  page_size: z.number().default(100),
  total_pages: z.number().default(1),
});
export type UnifiedQueryRecord = z.infer<typeof UnifiedQueryRecordSchema>;
export type UnifiedQueriesResponse = z.infer<typeof UnifiedQueriesResponseSchema>;
export const OpportunityResourceSchema = z.object({
  name: z.string(),
  uri: z.string(),
  resource_type: z.string(),
});
export const MoneySavingsSchema = z.object({ min: z.number(), max: z.number() });
export const SavingsAttributionSchema = z.object({
  start_date: z.string().optional(),
  end_date: z.string().optional(),
  altimate_estimated_cost_saving: z.number().optional(),
});
export const MetadataSchema = z.object({
  report: z.object({ actual_query_span: z.string(), type: z.string() }),
});
export const ResourceOpportunitySchema = z.object({
  opportunity_id: z.string(),
  resource_name: z.string(),
  resource_type: z.string(),
  opportunity_name: z.string(),
  description: z.string().nullable().optional(),
  last_seen: z.string(),
  first_seen: z.string(),
  count: z.number(),
  total_count: z.number().nullable().optional(),
  resources: z.array(OpportunityResourceSchema),
  money_savings: MoneySavingsSchema,
  time_savings: z.string(),
  effort: z.number(),
  owner: z.string().nullable().optional(),
  status: z.number(),
  linenumber: z.string().optional(),
  explanation_context: z.unknown().optional(),
  savings_attribution: SavingsAttributionSchema.optional(),
  metadata: MetadataSchema,
  instance_id: z.string().optional(),
});
export const ResourceOpportunitiesResponseSchema = z.object({
  data: z.array(ResourceOpportunitySchema),
});
export type OpportunityResource = z.infer<typeof OpportunityResourceSchema>;
export type MoneySavings = z.infer<typeof MoneySavingsSchema>;
export type ResourceOpportunity = z.infer<typeof ResourceOpportunitySchema>;
export type ResourceOpportunitiesResponse = z.infer<typeof ResourceOpportunitiesResponseSchema>;
// Initial Questions
export const InitialQuestionsSchema = z.object({
  questions: z.array(z.string()),
});
export type InitialQuestionsResponse = z.infer<typeof InitialQuestionsSchema>;

export interface InitialQuestionsParams {
  resource_type: string;
  resource_id: string;
  resource_name: string;
}

// API Parameter Types
interface DateRangeParams {
  start_date: string;
  end_date: string;
}
interface DateRangeGranularityParams extends DateRangeParams {
  granularity?: "day" | "week" | "month";
}

export interface SqlWarehousesParams extends Partial<DateRangeParams> {
  workspace_id?: string;
  state?: string;
  search?: string;
  page?: number;
  page_size?: number;
  sortAttribute?: string;
  sortOrder?: string;
  warehouse_names?: string[];
  warehouse_types?: string[];
  warehouse_sizes?: string[];
  workspace_ids?: string[];
}
export interface SqlWarehouseDetailParams {
  warehouse_id: string;
}
export interface SqlWarehouseCostTrendsParams extends DateRangeGranularityParams {
  workspace_id?: string;
}
export interface SqlWarehouseFilterParams {
  prefix?: string;
  page?: number;
  size?: number;
  workspace_id?: string;
  start_date?: string;
  end_date?: string;
}
export interface SqlWarehouseEventsParams extends DateRangeParams {
  event_type?: string;
}
export interface SqlWarehouseCostParams extends DateRangeGranularityParams {}
export interface SqlWarehouseUtilizationParams extends DateRangeGranularityParams {}
export interface UnifiedQueriesParams extends DateRangeParams {
  warehouse_id?: string;
  workspace_id?: string;
  user_name?: string;
  statement_type?: string;
  insight?: string;
  page?: number;
  page_size?: number;
}
export interface ResourceOpportunitiesParams extends DateRangeParams {
  resource_uri: string;
  resource_type: number;
  instance_id: number;
  status?: string;
}
